/**
 * OpenAI chat-completions client over an injected fetch (docs/agent.md §3).
 *
 * Two jobs, both fiddlier than they look:
 *
 *  1. SSE framing. Events are `data: {...}` lines separated by blank lines, but
 *     chunks arrive on arbitrary byte boundaries — a line can be split mid-JSON
 *     and multi-byte UTF-8 can straddle two reads. Providers also emit comment
 *     lines (OpenRouter sends `: OPENROUTER PROCESSING` keepalives) that must be
 *     ignored rather than parsed.
 *
 *  2. Tool-call accumulation. Tool calls stream as FRAGMENTS: the first delta
 *     carries `index`, `id` and `function.name`, then later deltas append more
 *     of `function.arguments` — one call's JSON arrives across many chunks.
 *     They are assembled by `index` and only emitted once the turn finishes.
 *     Some providers send a complete call in one delta; both shapes work here.
 */
import type { LlmMessage, LlmRequest, LlmStreamEvent, LlmToolCall, LlmTransport } from './types';

export interface OpenAiTransportOptions {
  /** Endpoint that accepts the chat-completions body. */
  url: string;
  fetchImpl?: typeof fetch;
  /** Extra headers — the proxy needs CSRF; a direct provider call needs auth. */
  headers?: () => Record<string, string>;
  credentials?: RequestCredentials;
  /** Sent verbatim; the app omits it because the proxy pins the model. */
  model?: string;
}

/**
 * Build a transport. In the app the url is the Django proxy (which holds the
 * key and pins the model); in evals it is the provider directly.
 */
export function createOpenAiTransport(opts: OpenAiTransportOptions): LlmTransport {
  const doFetch = opts.fetchImpl ?? fetch;

  return async function* transport(req: LlmRequest, signal: AbortSignal) {
    const body: Record<string, unknown> = {
      messages: req.messages,
    };
    if (opts.model) body.model = opts.model;
    if (req.tools?.length) body.tools = req.tools;
    if (req.toolChoice) body.tool_choice = req.toolChoice;
    if (req.maxTokens !== undefined) body.max_tokens = req.maxTokens;
    if (req.temperature !== undefined) body.temperature = req.temperature;
    // Only set by direct-to-provider callers; the proxy adds these itself and
    // ignores them coming from a client.
    if (opts.model) {
      body.stream = true;
      body.stream_options = { include_usage: true };
    }

    const res = await doFetch(opts.url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(opts.headers?.() ?? {}),
      },
      body: JSON.stringify(body),
      credentials: opts.credentials,
      signal,
    });

    if (!res.ok || !res.body) {
      throw await describeHttpError(res);
    }

    yield* parseSseStream(res.body, signal);
  };
}

/**
 * A transport failure the UI can branch on.
 *
 * `code` is the proxy's stable error code (backend/agent/views.py) — `disabled`,
 * `over_budget`, `in_flight`, `upstream_timeout` and so on. Carrying it as a
 * field rather than baking it into the message is what lets the UI say
 * something specific and decide whether a retry is even worth offering.
 */
export class LlmTransportError extends Error {
  readonly status?: number;
  readonly code?: string;
  readonly detail?: string;

  constructor(message: string, opts: { status?: number; code?: string; detail?: string } = {}) {
    super(message);
    this.name = 'LlmTransportError';
    this.status = opts.status;
    this.code = opts.code;
    this.detail = opts.detail;
  }
}

/** Turn an HTTP failure into something a human and the UI can act on. */
async function describeHttpError(res: Response): Promise<LlmTransportError> {
  let detail = '';
  let code: string | undefined;
  try {
    const text = await res.text();
    if (text) {
      try {
        const json = JSON.parse(text) as { errors?: string[]; code?: string; detail?: string };
        detail = json.errors?.join('; ') ?? json.detail ?? text;
        code = json.code;
      } catch {
        detail = text.slice(0, 500);
      }
    }
  } catch {
    /* body already consumed or unreadable */
  }
  return new LlmTransportError(
    `LLM request failed (HTTP ${res.status})${detail ? `: ${detail}` : ''}`,
    { status: res.status, code, detail },
  );
}

/**
 * Parse an SSE body into stream events. Exported for tests, which drive it with
 * a hand-built ReadableStream split at hostile boundaries.
 */
export async function* parseSseStream(
  body: ReadableStream<Uint8Array>,
  signal?: AbortSignal,
): AsyncGenerator<LlmStreamEvent> {
  const reader = body.getReader();
  // `stream: true` is what makes the decoder hold a partial multi-byte
  // character back until the rest of it arrives.
  const decoder = new TextDecoder();
  let buffer = '';

  const pending = new ToolCallAccumulator();
  let finishReason = 'stop';
  let sawToolCalls = false;

  try {
    for (;;) {
      if (signal?.aborted) return;
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let newline: number;
      while ((newline = buffer.indexOf('\n')) >= 0) {
        const line = buffer.slice(0, newline).trim();
        buffer = buffer.slice(newline + 1);
        if (!line) continue;
        // `:` prefixed lines are SSE comments (provider keepalives).
        if (line.startsWith(':')) continue;
        if (!line.startsWith('data:')) continue;
        const payload = line.slice('data:'.length).trim();
        if (payload === '[DONE]') continue;

        let chunk: ChatChunk;
        try {
          chunk = JSON.parse(payload) as ChatChunk;
        } catch {
          continue; // a malformed frame is not worth killing the run over
        }

        if (chunk.usage) {
          yield {
            type: 'usage',
            usage: {
              promptTokens: chunk.usage.prompt_tokens ?? 0,
              completionTokens: chunk.usage.completion_tokens ?? 0,
            },
          };
        }

        const choice = chunk.choices?.[0];
        if (!choice) continue;
        if (choice.finish_reason) finishReason = choice.finish_reason;

        const delta = choice.delta;
        if (!delta) continue;
        if (typeof delta.content === 'string' && delta.content) {
          yield { type: 'text-delta', text: delta.content };
        }
        if (delta.tool_calls?.length) {
          sawToolCalls = true;
          pending.absorb(delta.tool_calls);
        }
      }
    }
    // The read loop only emits COMPLETE lines. A provider that ends without a
    // trailing newline leaves its last frame sitting in the buffer — and if
    // that frame carried the tool calls, the loop sees an empty turn and reads
    // it as "the model finished talking": a silent no-op run. Flush the decoder
    // (releasing any held partial code point) and process what is left.
    buffer += decoder.decode();
    const tail = buffer.trim();
    if (tail.startsWith('data:')) {
      const payload = tail.slice('data:'.length).trim();
      if (payload && payload !== '[DONE]') {
        try {
          const chunk = JSON.parse(payload) as ChatChunk;
          if (chunk.usage) {
            yield {
              type: 'usage',
              usage: {
                promptTokens: chunk.usage.prompt_tokens ?? 0,
                completionTokens: chunk.usage.completion_tokens ?? 0,
              },
            };
          }
          const choice = chunk.choices?.[0];
          if (choice) {
            if (choice.finish_reason) finishReason = choice.finish_reason;
            if (typeof choice.delta?.content === 'string' && choice.delta.content) {
              yield { type: 'text-delta', text: choice.delta.content };
            }
            if (choice.delta?.tool_calls?.length) {
              sawToolCalls = true;
              pending.absorb(choice.delta.tool_calls);
            }
          }
        } catch {
          /* a genuinely truncated final frame is not worth failing the run over */
        }
      }
    }
  } finally {
    // releaseLock alone leaves the body undrained when a consumer breaks early
    // or the signal aborts; cancel releases the underlying connection.
    void reader.cancel().catch(() => {});
    reader.releaseLock();
  }

  if (sawToolCalls) {
    const calls = pending.finish();
    if (calls.length) yield { type: 'tool-calls', calls };
  }
  yield { type: 'finish', reason: finishReason };
}

interface ChatChunk {
  choices?: {
    delta?: {
      content?: string | null;
      tool_calls?: RawToolCallDelta[];
    };
    finish_reason?: string | null;
  }[];
  usage?: { prompt_tokens?: number; completion_tokens?: number } | null;
}

interface RawToolCallDelta {
  index?: number;
  id?: string;
  type?: string;
  function?: { name?: string; arguments?: string };
}

/**
 * Reassembles streamed tool calls. Keyed by `index` because that is the only
 * field guaranteed on every fragment — `id` and `name` typically appear once,
 * on the first fragment, and `arguments` is split across the rest.
 */
class ToolCallAccumulator {
  private byIndex = new Map<number, { id: string; name: string; args: string }>();

  absorb(deltas: RawToolCallDelta[]): void {
    for (const delta of deltas) {
      const index = delta.index ?? 0;
      let entry = this.byIndex.get(index);
      if (!entry) {
        entry = { id: '', name: '', args: '' };
        this.byIndex.set(index, entry);
      }
      if (delta.id) entry.id = delta.id;
      if (delta.function?.name) entry.name = delta.function.name;
      if (delta.function?.arguments) entry.args += delta.function.arguments;
    }
  }

  finish(): LlmToolCall[] {
    return [...this.byIndex.entries()]
      .sort((a, b) => a[0] - b[0])
      .filter(([, e]) => e.name)
      .map(([index, e]) => ({
        // A provider that omits ids still needs stable, distinct ones, because
        // tool result messages are matched back by tool_call_id.
        id: e.id || `call_${index}`,
        type: 'function' as const,
        function: { name: e.name, arguments: e.args || '{}' },
      }));
  }
}

/**
 * The app's transport: posts to the Django proxy. No key, no model — the proxy
 * owns both (docs/agent.md §5).
 */
export function createProxyTransport(fetchImpl?: typeof fetch): LlmTransport {
  return createOpenAiTransport({
    url: '/api/agent/chat',
    fetchImpl,
    credentials: 'include',
    headers: (): Record<string, string> => {
      const token = readCookie('csrftoken');
      return token ? { 'X-CSRFToken': token } : {};
    },
  });
}

/** Same self-healing read-per-request approach as api/backend.ts. */
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const c = part.trim();
    if (c.startsWith(prefix)) return decodeURIComponent(c.slice(prefix.length));
  }
  return null;
}

/** Helper for building the assistant message that carries tool calls. */
export function assistantToolCallMessage(text: string, calls: LlmToolCall[]): LlmMessage {
  return { role: 'assistant', content: text || null, tool_calls: calls };
}
